import moment from 'moment';

class CreateContractModalController {
  constructor($scope, $uibModalInstance, $rootScope, $http, customerId) {
    "ngInject";
    this.name = 'CreateContractModal';

    $scope.contract = {
      contractName: '',
      shortName: '',
      customerId: customerId || null,
      contractClass: '',
      contractType: '',
      primeContractNumber: '',
      subContractNumber: '',
      startDate: null,
      endDate: null,
      netPaymentTerms: '',
      invoiceFrequency: '',
      invoiceWeekStart: '',
      timeEntryRoundingInterval: null,
      allowSubcontracting: false
    };

    // Date text inputs for display
    $scope.startDateText = '';
    $scope.endDateText = '';
    $scope.startDateTextUpdating = false;
    $scope.endDateTextUpdating = false;

    // Customer ID is passed via resolve and cannot be changed
    // No need to load customers list

    $scope.contractTypeOptions = [
      { value: 'T&M', text: 'Time and Materials' },
      { value: 'FFP', text: 'Firm Fixed Price' },
      { value: 'FFP LOE', text: 'Firm Fixed Price Level of Effort' },
      { value: 'CPFF', text: 'Cost Plus Fixed Fee' },
      { value: 'CPAF', text: 'Cost Plus Award Fee' },
    ];

    $scope.paymentTermOptions = [
      {value: '15', text: 'Net 15'},
      {value: '30', text: 'Net 30'},
      {value: '45', text: 'Net 45'}
    ];

    $scope.invoiceFrequencyOptions = [
      {value: 'MONTHLY', text: 'Monthly'},
      {value: 'SEMI_MONTHLY', text: 'Semi-Monthly'},
      {value: 'WEEKLY', text: 'Weekly'}
    ];

    $scope.invoiceWeekStartOptions = [
      {value: 'SUNDAY', text: 'Sunday'},
      {value: 'MONDAY', text: 'Monday'}
    ];

    $scope.timeEntryRoundingIntervalOptions = [
      { value: .1, text: '1/10 of an Hour (6 Minute Intervals)' },
      { value: .25, text: '1/4 of an Hour (15 Minute Intervals)' },
    ];

    // Customer ID is set from resolve and cannot be changed
    $scope.contract.customerId = customerId;

    // Date handling methods
    $scope.onStartDateSelected = function(selectedDate) {
      $scope.startDateTextUpdating = true;
      $scope.contract.startDate = moment(selectedDate).format('YYYY-MM-DD');
      $scope.startDateText = moment(selectedDate).format('MMM DD, YYYY');
      // Clear end date if it's before start date
      if ($scope.contract.endDate && moment($scope.contract.endDate).isBefore($scope.contract.startDate)) {
        $scope.contract.endDate = null;
        $scope.endDateText = '';
      }
      $scope.startDateTextUpdating = false;
    };

    $scope.onEndDateSelected = function(selectedDate) {
      $scope.endDateTextUpdating = true;
      $scope.contract.endDate = moment(selectedDate).format('YYYY-MM-DD');
      $scope.endDateText = moment(selectedDate).format('MMM DD, YYYY');
      $scope.endDateTextUpdating = false;
    };

    $scope.onStartDateTextChange = function() {
      if (!$scope.startDateText || $scope.startDateText.trim() === '') {
        $scope.contract.startDate = null;
        return;
      }
      const parsedDate = moment($scope.startDateText, ['MMM DD, YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD', 'MM-DD-YYYY'], true);
      if (parsedDate.isValid()) {
        $scope.startDateTextUpdating = true;
        $scope.contract.startDate = parsedDate.format('YYYY-MM-DD');
        $scope.startDateText = parsedDate.format('MMM DD, YYYY');
        // Clear end date if it's before start date
        if ($scope.contract.endDate && moment($scope.contract.endDate).isBefore($scope.contract.startDate)) {
          $scope.contract.endDate = null;
          $scope.endDateText = '';
        }
        $scope.startDateTextUpdating = false;
      }
    };

    $scope.onEndDateTextChange = function() {
      if (!$scope.endDateText || $scope.endDateText.trim() === '') {
        $scope.contract.endDate = null;
        return;
      }
      const parsedDate = moment($scope.endDateText, ['MMM DD, YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD', 'MM-DD-YYYY'], true);
      if (parsedDate.isValid()) {
        $scope.endDateTextUpdating = true;
        $scope.contract.endDate = parsedDate.format('YYYY-MM-DD');
        $scope.endDateText = parsedDate.format('MMM DD, YYYY');
        $scope.endDateTextUpdating = false;
      }
    };

    $scope.getEndDateMinDate = function() {
      if ($scope.contract.startDate) {
        return moment($scope.contract.startDate).format('YYYY-MM-DD');
      }
      return null;
    };

    // Watch for changes to dates and update text inputs (only when not manually editing)
    $scope.$watch(() => $scope.contract.startDate, (newVal, oldVal) => {
      if (!$scope.startDateTextUpdating && newVal !== oldVal) {
        if (newVal) {
          $scope.startDateText = moment(newVal).format('MMM DD, YYYY');
        } else {
          $scope.startDateText = '';
        }
      }
    });

    $scope.$watch(() => $scope.contract.endDate, (newVal, oldVal) => {
      if (!$scope.endDateTextUpdating && newVal !== oldVal) {
        if (newVal) {
          $scope.endDateText = moment(newVal).format('MMM DD, YYYY');
        } else {
          $scope.endDateText = '';
        }
      }
    });

    // Validation function
    $scope.isFormValid = function() {
      const nameValid = $scope.contract.contractName && $scope.contract.contractName.trim() !== '';
      const shortNameValid = $scope.contract.shortName && $scope.contract.shortName.trim() !== '';
      const customerValid = customerId !== null && customerId !== undefined;
      const classificationValid = $scope.contract.contractClass && ($scope.contract.contractClass === 'prime' || $scope.contract.contractClass === 'sub');
      const contractTypeValid = $scope.contract.contractType && $scope.contract.contractType.trim() !== '';
      const primeContractNumberValid = $scope.contract.primeContractNumber && $scope.contract.primeContractNumber.trim() !== '';
      const subcontractNumberValid = $scope.contract.contractClass === 'prime' || ($scope.contract.subContractNumber && $scope.contract.subContractNumber.trim() !== '');
      const startDateValid = $scope.contract.startDate && $scope.contract.startDate !== null && $scope.contract.startDate.trim() !== '';
      const endDateValid = $scope.contract.endDate && $scope.contract.endDate !== null && $scope.contract.endDate.trim() !== '';
      const paymentTermsValid = $scope.contract.netPaymentTerms && $scope.contract.netPaymentTerms.trim() !== '';
      const invoiceFrequencyValid = $scope.contract.invoiceFrequency && $scope.contract.invoiceFrequency.trim() !== '';
      const invoiceWeekStartValid = $scope.contract.invoiceFrequency !== 'WEEKLY' || ($scope.contract.invoiceWeekStart && $scope.contract.invoiceWeekStart.trim() !== '');
      const roundingIntervalValid = $scope.contract.timeEntryRoundingInterval !== null && $scope.contract.timeEntryRoundingInterval !== undefined;
      
      return nameValid && shortNameValid && customerValid && classificationValid && contractTypeValid && 
             primeContractNumberValid && subcontractNumberValid && startDateValid && endDateValid && 
             paymentTermsValid && invoiceFrequencyValid && invoiceWeekStartValid && roundingIntervalValid;
    };

    let self = this;
    $scope.addContract = function () {
      if (!$scope.isFormValid()) {
        return;
      }

      // Clean up the data before sending
      const contractData = {
        contractName: $scope.contract.contractName.trim(),
        shortName: $scope.contract.shortName.toUpperCase().trim(),
        customerId: customerId, // Always use the resolved customerId
        contractClass: $scope.contract.contractClass || null,
        contractType: $scope.contract.contractType || null,
        primeContractNumber: $scope.contract.primeContractNumber ? $scope.contract.primeContractNumber.trim() : null,
        subContractNumber: $scope.contract.subContractNumber ? $scope.contract.subContractNumber.trim() : null,
        startDate: $scope.contract.startDate || null,
        endDate: $scope.contract.endDate || null,
        netPaymentTerms: $scope.contract.netPaymentTerms || null,
        invoiceFrequency: $scope.contract.invoiceFrequency || null,
        invoiceWeekStart: $scope.contract.invoiceWeekStart || null,
        timeEntryRoundingInterval: $scope.contract.timeEntryRoundingInterval !== null && $scope.contract.timeEntryRoundingInterval !== undefined ? $scope.contract.timeEntryRoundingInterval : null,
        allowSubcontracting: $scope.contract.allowSubcontracting === true
      };

      $uibModalInstance.close(contractData);
    };

    $scope.cancel = function () {
      $uibModalInstance.dismiss();
    };
  }
}

export default CreateContractModalController;




// WEBPACK FOOTER //
// ./client/pages/admin/customer-details/modals/createContractModal/createContractModal.controller.js